import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { v4 } from 'uuid';
import { ISaveAnnotationDraftRequest, ISubmitAnnotationForReviewRequest } from '../../../modules/editor/models/Requests';
import { IBatchAnnotationApiService } from '../../../modules/editor/services/BatchAnnotationApi.service';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { buildBounds, getLatLngsForGeojson } from '../../helpers/geometry/polygon.helpers';
import { ACDLError } from '../../models/error.model';
import { EventListeningDisposer, IEventBus } from '../../services/eventBus.service';
import { IProjectHubService } from '../../services/projectHub.service';
import { ITimerService } from '../../services/timer.service';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { IAnnotationService } from './annotation.service';
import { IAnnotationApiService } from './annotationApi.service';
import { AnnotationStatus, IAnnotationDto, IImage, IQuestionModelDto, ISegmentation } from './annotations.interface';
import { IAnnotationsStore } from './annotations.store';
import { IAnnotationUiStore } from './annotationUi.store';
import { BaseAnnotationControl, IBaseAnnotationControl } from './baseAnnotationControl';
import { IFreeDrawSegmentationService } from './freeDrawSegmentation.service';
import { IImagesQueueService } from './imagesQueueServiceBase';
import { cleanClone, cleanClones, ImageScopeName, IQuestionModel } from './question.model';
import { IAnnotationTypeBl } from './submodules/annotationTypes/annotationType.bl';
import { ClarificationAddedEvent, ClarificationAddedEventType } from './submodules/clarifications/events/ClarificationAddedEvent';
import { IUndoRedoHistory } from './undoRedoHistory.service';

export interface IAnnotationCreationService extends IBaseAnnotationControl {
  setupAsync(projectId: string, imagesQueueService: IImagesQueueService): Promise<void>;
  safelyLeaveAnnotationAsync(): Promise<boolean>;
  unlockImagesAsync(): Promise<void>;
  skipAnnotationsAsync(): Promise<boolean | undefined>;
  batchCorrectAnnotationAsync(): Promise<boolean>;
  batchCorrectAndSubmitAnnotationForReviewAsync(): Promise<boolean>;
  batchSaveAnnotationDraftAsync(): Promise<boolean>;
  batchAbandonAnnotationDraftAsync(saveData: boolean): Promise<boolean>;
  batchSubmitAnnotationForReviewAsync(): Promise<boolean>;
  batchDiscardAnnotationDuringAnnotationAsync(): Promise<boolean>;
}

export namespace IAnnotationCreationService {
  export const $: interfaces.ServiceIdentifier<IAnnotationCreationService> = Symbol('ANNOTATION_CREATION_SERVICE');
}

@injectable()
export class AnnotationCreationService extends BaseAnnotationControl implements IAnnotationCreationService {
  private eventListenersDisposer: EventListeningDisposer[] = [];

  constructor(
    @inject(IFreeDrawSegmentationService.$) private readonly freeDrawSegmentationService: IFreeDrawSegmentationService,
    @inject(IProjectHubService.$) private readonly projectHubService: IProjectHubService,
    @inject(IAnnotationApiService.$) private readonly annotationApiService: IAnnotationApiService,
    @inject(IAnnotationService.$) private readonly annotationService: IAnnotationService,
    @inject(IAnnotationTypeBl.$) private readonly annotationTypeBl: IAnnotationTypeBl,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore,
    @inject(IUndoRedoHistory.$) private readonly undoRedoService: IUndoRedoHistory,
    @inject(INotificationsService.$) notificationsService: INotificationsService,
    @inject(IAnnotationUiStore.$) uiStore: IAnnotationUiStore,
    @inject(IAnnotationsStore.$) annotationsStore: IAnnotationsStore,
    @inject(ITimerService.$) public readonly timer: ITimerService,
    @inject(IEventBus.$) public readonly eventBus: IEventBus,
    @inject(IBatchAnnotationApiService.$) private readonly batchAnnotationApiService: IBatchAnnotationApiService
  ) {
    super(uiStore, notificationsService, annotationsStore);

    makeObservable(this, {
      setupAsync: action.bound,
      batchSubmitAnnotationForReviewAsync: action.bound,
      batchCorrectAnnotationAsync: action.bound,
      batchCorrectAndSubmitAnnotationForReviewAsync: action.bound,
      batchSaveAnnotationDraftAsync: action.bound,
      batchAbandonAnnotationDraftAsync: action.bound,
      batchDiscardAnnotationDuringAnnotationAsync: action.bound,
      skipAnnotationsAsync: action.bound,
      unlockImagesAsync: action.bound,
      requestQuestionsAsync: action.bound,
      refreshQuestionsIfNeeded: action.bound,
      loadAnnotations: action.bound,
    });
  }

  handleImageDisplayed = (image: IImage): void => {
    this.loadAnnotations(image);
  };

  async setupAsync(projectId: string, imagesQueueService: IImagesQueueService): Promise<void> {
    this.annotationsStore.projectId = projectId;
    this.imagesQueueService = imagesQueueService;
    await this.projectHubService.initializeAsync();
    this.initReactions();
    this.imagesQueueService.start(true);
    await Promise.all([this.annotationTypeBl.creationStarted(projectId), this.requestQuestionsAsync(), this.updateAnnotationProgressAsync()]);
    this.displayNextImage();

    this.addEventListeners();
  }

  displayNextImage(): void {
    this.displayImageFromQueue();
    this.timer.startTimer();
  }

  private addEventListeners(): void {
    this.eventListenersDisposer.push(
      this.eventBus.addListener<ClarificationAddedEvent>(() => {
        this.clarificationAddedListener();
      }, ClarificationAddedEventType)
    );
  }

  private clearEventListeners(): void {
    this.eventListenersDisposer.forEach((d) => {
      d();
    });
    this.eventListenersDisposer = [];
  }

  async batchSubmitAnnotationForReviewAsync(): Promise<boolean> {
    this.uiStore.setIsInValidation(true);
    if (!this.annotationService.validateAnnotations()) {
      await this.annotationTypeBl.handleSubmitAnnotationInCreation();
      return false;
    }

    const task = this.batchAnnotationApiService.submitForReview(this.mapToSubmitAnnotationRequest());

    this.freeDrawSegmentationService.clear();
    this.annotationService.fixRectangles();

    this.cleanupAfterSave();

    this.displayImageFromQueue();

    const result = await task;

    if (result instanceof ACDLError) {
      await this.handleErrors(result);
      return false;
    }

    await this.updateAnnotationProgressAsync();
    this.refreshQuestionsIfNeeded();

    return true;
  }

  safelyLeaveAnnotationAsync = async (): Promise<boolean> => {
    return this.annotationsStore.annotationStatus === AnnotationStatus.REJECTED ? this.batchCorrectAnnotationAsync() : this.batchSaveAnnotationDraftAsync();
  };

  async batchCorrectAnnotationAsync(): Promise<boolean> {
    this.uiStore.setIsInValidation(true);
    if (!this.annotationService.validateAnnotations() || !this.annotationsStore.id) {
      await this.annotationTypeBl.handleSaveAnnotationsInCreation();
      return false;
    }

    this.freeDrawSegmentationService.clear();
    this.annotationService.fixRectangles();

    const task = this.batchAnnotationApiService.correctAsync({
      id: this.annotationsStore.id,
      duration: this.timer.duration,
      annotation: this.mapToAnnotationDto().annotation,
    });

    return await this.finishWorkWithImage(task);
  }

  async batchCorrectAndSubmitAnnotationForReviewAsync(): Promise<boolean> {
    this.uiStore.setIsInValidation(true);
    if (!this.annotationService.validateAnnotations() || !this.annotationsStore.id) {
      await this.annotationTypeBl.handleSaveAnnotationsInCreation();
      return false;
    }

    this.freeDrawSegmentationService.clear();
    this.annotationService.fixRectangles();

    const task = this.batchAnnotationApiService.correctAndSubmitForReviewAsync({
      id: this.annotationsStore.id,
      duration: this.timer.duration,
      annotation: this.mapToAnnotationDto().annotation,
    });

    return await this.finishWorkWithImage(task);
  }

  async batchSaveAnnotationDraftAsync(): Promise<boolean> {
    this.freeDrawSegmentationService.clear();
    this.annotationService.fixRectangles();

    const createAnnotationTask = this.batchAnnotationApiService.saveDraft(this.mapToAnnotationDto());

    this.cleanupAfterSave();

    const result = await createAnnotationTask;

    if (result instanceof ACDLError) {
      await this.handleErrors(result);
    }

    return result instanceof ACDLError;
  }

  async batchAbandonAnnotationDraftAsync(saveData: boolean): Promise<boolean> {
    if (!this.annotationsStore.id) {
      return false;
    }

    this.freeDrawSegmentationService.clear();
    this.annotationService.fixRectangles();

    const data = saveData ? this.mapToAnnotationDto().annotation : null;
    const task = this.batchAnnotationApiService.abandonDraft({ id: this.annotationsStore.id, annotation: data, duration: this.timer.duration });

    return await this.finishWorkWithImage(task);
  }

  async batchDiscardAnnotationDuringAnnotationAsync(): Promise<boolean> {
    if (!this.annotationsStore.id) {
      return false;
    }

    this.freeDrawSegmentationService.clear();
    this.annotationService.fixRectangles();

    const request = { id: this.annotationsStore.id, duration: this.timer.duration };
    const task = this.batchAnnotationApiService.discardDuringAnnotation(request);

    return await this.finishWorkWithImage(task);
  }

  async skipAnnotationsAsync(): Promise<boolean | undefined> {
    if (!(await this.freeDrawSegmentationService.clearAsync()) || !this.annotationsStore.image?.id) {
      return undefined;
    }

    this.annotationService.fixRectangles();

    const task = this.annotationApiService.unlockImageAsync(this.annotationsStore.image.id, this.annotationsStore.projectId, true);

    return await this.finishWorkWithImage(task);
  }

  private async finishWorkWithImage(task: Promise<void | ACDLError>): Promise<boolean> {
    this.cleanupAfterSave();

    this.displayImageFromQueue();

    const result = await task;

    if (result instanceof ACDLError) {
      await this.handleErrors(result);
      return false;
    }

    await this.updateAnnotationProgressAsync();
    this.refreshQuestionsIfNeeded();
    this.annotationTypeBl.handleFinishWorkWithImageInCreation();

    return true;
  }

  async unlockImagesAsync(): Promise<void> {
    if (this.annotationsStore.projectId) {
      await this.annotationApiService.unlockUserImagesForProjectAsync(this.annotationsStore.projectId);
    }
  }

  async requestQuestionsAsync(): Promise<void> {
    const result = await this.annotationApiService.requestQuestionsAsync(this.annotationsStore.projectId);
    if (result instanceof Error) {
      throw result;
    }
    this.annotationsStore.questions.replace([...result]);
    this.annotationService.deselectSegmentation();
  }

  refreshQuestionsIfNeeded(): void {
    this.annotationService.deselectSegmentation();
    if (this.projectHubService.didQuestionsChangedFor(this.annotationsStore.projectId)) {
      const result = this.projectHubService.popNewQuestionsFor(this.annotationsStore.projectId);
      this.annotationsStore.questions.replace(cleanClones(result));
    }
  }

  loadAnnotations(image: IImage): void {
    this.annotationsStore.setHiddenSegmentations([]);

    const annotationInfoResult = image.annotations;

    this.annotationsStore.annotationStatus = annotationInfoResult?.status;

    if (!annotationInfoResult) {
      return;
    }

    this.annotationsStore.annotationStatus = annotationInfoResult.status;

    const annotations = annotationInfoResult.annotations;
    if (annotations === undefined) {
      throw new ACDLError('No annotations found', undefined);
    }

    this.annotationsStore.id = annotationInfoResult.id;
    const segmentations: ISegmentation[] = annotations.segmentations.map((s) => {
      const feature = {
        ...s.feature,
        id: v4(),
        color: this.annotationService.getAnnotationTypeColor(s.feature.properties?.annotationTypeId),
        featureType: s.feature.properties?.featureType,
      };

      const latlngs = getLatLngsForGeojson(feature);

      return {
        feature,
        latlngs,
        id: s.id,
        questions: this.mapQuestions(s.feature.properties?.annotationTypeId, s.questions),
        bbox: buildBounds(latlngs),
        priority: s.priority,
      } as ISegmentation;
    });

    this.annotationsStore.setSegmentations(segmentations.filter((s) => s.feature.color !== 'black'));
    this.annotationsStore.questions.replace(this.mapQuestions(undefined, annotationInfoResult.annotations.questions));
    this.annotationService.deselectSegmentation();
  }

  dispose(): void {
    super.dispose();
    this.imagesQueueService.clear();
    this.annotationService.clear();
    this.freeDrawSegmentationService.clear();
    this.annotationTypeBl.creationFinished();
    this.clearEventListeners();
  }

  private async updateAnnotationProgressAsync(): Promise<void> {
    const result = await this.annotationApiService.getAnnotationProgressAsync(this.annotationsStore.projectId);
    if (result instanceof ACDLError) {
      return;
    }
    this.annotationsStore.annotationProgress = result;
  }

  private mapQuestions(annotationTypeId: string | undefined, questionsDtos: IQuestionModelDto[]): IQuestionModel[] {
    const currentQuestions = annotationTypeId
      ? this.annotationsStore.questions.filter((q) => q.scopes.includes(annotationTypeId))
      : this.annotationsStore.questions;

    const models: IQuestionModel[] = [];
    for (const currentQuestion of currentQuestions) {
      const existingAnswer = questionsDtos.find((x) => x.id === currentQuestion.id);
      models.push(cleanClone(currentQuestion, existingAnswer));
    }

    return models;
  }

  private cleanupAfterSave(): void {
    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();
    this.freeDrawSegmentationService.clear();
    this.annotationService.clearAnnotations();
    this.annotationService.clearAnswers();
    this.uiStore.setIsInValidation(false);
  }

  private async handleErrors(e: ACDLError): Promise<void> {
    if (e.isBadRequestWithCode(['PROJECT_NOT_FOUND'])) {
      this.annotationsStore.image = undefined;
      this.imagesQueueService.areAnyImagesToLoad = false;
    } else if (e.isBadRequestWithCode(['IMAGE_ALREADY_LOCKED'])) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'image_is_locked_by_another_user'));
    } else {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'something_went_wrong'));
    }

    return;
  }

  private mapToSubmitAnnotationRequest(): ISubmitAnnotationForReviewRequest {
    if (!this.annotationsStore.image?.id) {
      throw 'Invalid image id';
    }

    const id = this.annotationsStore.id ?? v4();
    return {
      id,
      projectId: this.annotationsStore.projectId,
      imageId: this.annotationsStore.image.id,
      duration: this.timer.duration,
      annotation: {
        id,
        questions: this.annotationsStore.questions
          .filter((q) => q.scopes.includes(ImageScopeName))
          .map((x) => ({ id: x.id, answer: x.answer, answers: x.answers.map((a) => ({ id: a.id, selected: a.selected })) })),
        segmentations: this.annotationsStore.segmentations.map((s) => ({
          id: s.id,
          questions: s.questions,
          feature: {
            type: s.feature.type,
            properties: s.feature.properties,
            geometry: s.feature.geometry,
          },
          priority: 0,
        })),
      },
    };
  }

  private mapToAnnotationDto(): ISaveAnnotationDraftRequest {
    if (!this.annotationsStore.image?.id) {
      throw 'Invalid image id';
    }
    const id = this.annotationsStore.id ?? v4();
    return {
      id,
      projectId: this.annotationsStore.projectId,
      imageId: this.annotationsStore.image.id,
      duration: this.timer.duration,
      annotation: {
        id,
        questions: this.annotationsStore.questions
          .filter((q) => q.scopes.includes(ImageScopeName))
          .map((q) => ({ id: q.id, answer: q.answer, answers: q.answers.map((a) => ({ id: a.id, selected: a.selected })) })),
        segmentations: this.annotationsStore.segmentations.map((s) => ({
          id: s.id ?? v4(),
          questions: s.questions,
          feature: {
            type: s.feature.type,
            properties: s.feature.properties,
            geometry: s.feature.geometry,
          },
          priority: s.priority,
        })),
      } as IAnnotationDto,
    };
  }

  private clarificationAddedListener(): void {
    if (this.undoRedoService.canUndo) {
      this.freeDrawSegmentationService.clear();
      this.annotationService.fixRectangles();
    }

    this.cleanupAfterSave();
    this.displayNextImage();
  }
}
